import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Check, Sparkles, Zap, Crown } from "lucide-react";

const planIcons = { free: Sparkles, pro: Zap, agency: Crown };

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/pricing/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);

  const checkout = async (planId) => {
    if (!user) {
      toast.info("Accedi prima di fare l'upgrade");
      navigate("/login");
      return;
    }
    if (planId === "free") {
      toast.info("Sei già sul piano Free");
      return;
    }
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/stripe/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore checkout");
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400] mb-4">
            · Pricing
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter text-white leading-[0.95] mb-6">
            Scegli il piano<br />che scala con te.
          </h1>
          <p className="text-zinc-400 font-body text-lg">
            Free per iniziare, Pro per creatori e professionisti.
            Annulla quando vuoi.
          </p>
          {user && (
            <div className="inline-flex items-center gap-2 mt-6 px-3 py-1.5 border border-zinc-800 bg-zinc-950 rounded-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF4400] pulse-dot" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                Piano attuale:
              </span>
              <span className="font-display font-bold text-white text-xs" data-testid="pricing-current-plan">
                {(user.plan || "free").toUpperCase()}
              </span>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
          {plans.map((plan) => {
            const Icon = planIcons[plan.id] || Sparkles;
            const isCurrent = user?.plan === plan.id;
            const isPopular = plan.id === "pro";
            return (
              <div
                key={plan.id}
                data-testid={`pricing-card-${plan.id}`}
                className={`relative border rounded-sm p-8 transition-all duration-300 ${
                  isPopular
                    ? "border-[#FF4400] bg-gradient-to-b from-[#FF4400]/10 to-transparent coral-glow"
                    : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-8 bg-[#FF4400] text-white px-3 py-1 text-[10px] font-mono uppercase tracking-widest font-bold rounded-sm">
                    Più scelto
                  </div>
                )}
                <div className="flex items-center justify-between mb-6">
                  <Icon className={`w-6 h-6 ${isPopular ? "text-[#FF4400]" : "text-zinc-500"}`} />
                  {isCurrent && (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400]">
                      Attivo
                    </span>
                  )}
                </div>

                <h3 className="font-display font-black text-3xl text-white tracking-tighter mb-1">
                  {plan.name}
                </h3>
                {plan.id === "free" && (
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400] mb-1">
                    · Solo i primi 30 giorni
                  </div>
                )}
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="font-display font-black text-5xl text-white tracking-tighter">
                    €{plan.price}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-zinc-500 font-body text-sm">/mese</span>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-300 font-body">
                      <Check className="w-4 h-4 text-[#FF4400] mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => checkout(plan.id)}
                  disabled={isCurrent || loadingPlan === plan.id}
                  data-testid={`pricing-select-${plan.id}`}
                  className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 font-display font-bold rounded-sm transition-all ${
                    isCurrent
                      ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                      : isPopular
                      ? "bg-[#FF4400] hover:bg-[#FF6633] text-white"
                      : "border border-zinc-700 hover:border-white text-white hover:bg-zinc-900"
                  }`}
                >
                  {isCurrent
                    ? "Piano attivo"
                    : loadingPlan === plan.id
                    ? "Redirect..."
                    : plan.id === "free"
                    ? "Inizia gratis"
                    : `Upgrade a ${plan.name}`}
                </button>
              </div>
            );
          })}
        </div>

        <ImagePacksSection user={user} navigate={navigate} />

        <div className="text-center mt-16 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
          · Pagamenti sicuri via Stripe · EUR · Annulli quando vuoi ·
        </div>
      </div>
    </div>
  );
};

const ImagePacksSection = ({ user, navigate }) => {
  const [packs, setPacks] = useState([]);
  const [loadingPack, setLoadingPack] = useState(null);

  useEffect(() => {
    api.get("/pricing/image-packs").then((r) => setPacks(r.data.packs)).catch(() => {});
  }, []);

  const buy = async (packId) => {
    if (!user) { toast.info("Accedi prima"); navigate("/login"); return; }
    setLoadingPack(packId);
    try {
      const { data } = await api.post("/stripe/buy-images", {
        pack_id: packId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore checkout");
      setLoadingPack(null);
    }
  };

  if (!packs.length) return null;
  const usable = user?.images_used_this_month != null;

  return (
    <div className="mt-16 border border-zinc-800 bg-zinc-950 rounded-sm p-7 md:p-10" data-testid="image-packs-section">
      <div className="text-center mb-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400] mb-2">Pacchetti immagini extra</div>
        <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white">
          Compra solo quello che serve
        </h2>
        <p className="text-zinc-500 font-body text-sm mt-2 max-w-md mx-auto">
          Pagamento una tantum, le immagini extra non scadono mai.
        </p>
      </div>

      {usable && user.extra_images_balance > 0 && (
        <div className="text-center mt-4 mb-2">
          <span className="inline-block px-3 py-1 border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 rounded-sm font-mono text-[10px] uppercase tracking-widest font-bold">
            Saldo extra: {user.extra_images_balance} immagini
          </span>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        {packs.map((p) => {
          const perImg = (p.price / p.images).toFixed(2);
          const popular = p.id === "pack_150";
          return (
            <div key={p.id} data-testid={`pricing-pack-${p.id}`} className={`border ${popular ? "border-[#FF4400]" : "border-zinc-800"} bg-black/30 rounded-sm p-6 relative flex flex-col`}>
              {popular && <div className="absolute -top-2 right-4 px-2 py-0.5 bg-[#FF4400] text-white font-mono text-[9px] uppercase tracking-widest font-bold">Best value</div>}
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{p.name}</div>
              <div className="flex items-baseline gap-1 mb-1">
                <div className="font-display font-black text-4xl text-white tracking-tighter">€{p.price}</div>
              </div>
              <div className="font-display font-bold text-[#FF4400]">{p.images} immagini AI</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600 mb-5">€{perImg}/immagine</div>
              <button
                onClick={() => buy(p.id)}
                disabled={loadingPack === p.id}
                data-testid={`pricing-pack-cta-${p.id}`}
                className={`mt-auto w-full px-5 py-3 rounded-sm font-display font-bold transition-colors ${popular ? "bg-[#FF4400] hover:bg-[#FF6633] text-white" : "border border-zinc-700 hover:border-white text-white hover:bg-zinc-900"}`}
              >
                {loadingPack === p.id ? "Redirect..." : `Acquista per €${p.price}`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Pricing;
